import { reactive, toRefs, watch } from 'vue';
import { useApiAuth, useApiNoAuth } from '@/hooks';
import { IBatch, IBatchDetail, IClaimBatchItem } from '@/types';
import { claimQueryKey } from '@/data';

interface IBatchState {
  loading: boolean;
  error: string | null;
  batchDetails: IBatchDetail[];
}

const batchState = reactive<IBatchState>({
  loading: false,
  error: null,
  batchDetails: [],
});

export const useBatch = (batchId?: string, supplierId?: number, batchCode?: string) => {
  const {
    loading: isUploading,
    data: uploadResponse,
    error: uploadError,
    success: uploadSuccess,
    post: uploadCSV,
  } = useApiAuth<void>('/claim/batch');

  const {
    loading: loadingBatchMedia,
    data: batchMediaData,
    error: batchMediaError,
    get: getBatchMedia,
  } = useApiNoAuth<IClaimBatchItem[]>(`/claim/batch/${batchId}/media`);

  const {
    loading: isSubmittingBatch,
    error: submitBatchError,
    success: submitBatchSuccess,
    post: submitBatch,
  } = useApiAuth<void>(`/supplier/${supplierId}/claim/batch`);

  const submitBatchBySupplier = (payload: IBatch) => {
    submitBatch(payload);
  };

  const {
    loading: isClosingBatch,
    error: closeBatchError,
    success: closeBatchSuccess,
    post: closeBatch,
  } = useApiAuth<IClaimBatchItem>(`/claim/batch/${batchCode}`);

  const {
    loading: loadingBatchDetailsBySupplier,
    data: batchDetailsBySupplier,
    error: batchDetailsBySupplierError,
    get: getBatchDetails,
  } = useApiAuth<IBatchDetail[]>(`/claim/batch_metadata`);

  const getBatchDetailsBySupplier = (supplierId: number) => {
    getBatchDetails({ [claimQueryKey.supplier]: supplierId });
  };

  const resetBatchDetails = () => {
    batchState.batchDetails = [];
  };

  const getBatchMediaDownloadLink = () =>
    `${process.env.VUE_APP_ROOT_API}/claim/batch/${batchId}/media/download`;

  watch(
    [loadingBatchDetailsBySupplier, batchDetailsBySupplier, batchDetailsBySupplierError],
    () => {
      batchState.loading = loadingBatchDetailsBySupplier.value;
      batchState.error = batchDetailsBySupplierError.value;
      batchState.batchDetails = batchDetailsBySupplier.value || [];
    }
  );

  return {
    ...toRefs(batchState),
    isUploading,
    uploadResponse,
    uploadError,
    uploadSuccess,
    uploadCSV,
    loadingBatchMedia,
    batchMediaData,
    batchMediaError,
    getBatchMedia,
    getBatchMediaDownloadLink,
    isSubmittingBatch,
    submitBatchError,
    submitBatchSuccess,
    submitBatchBySupplier,
    getBatchDetailsBySupplier,
    resetBatchDetails,
    isClosingBatch,
    closeBatchError,
    closeBatchSuccess,
    closeBatch,
  };
};
